import React, {
  useEffect,
  useState,
  useContext,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useParams } from "react-router-dom";
import { db, storage } from "../firebaseConfig";
import {
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
} from "firebase/firestore";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Calendar,
  CheckCircle,
  AlertCircle,
  Clock,
  User,
  DollarSign,
  Save,
  FileText,
  Activity,
  Briefcase,
  Globe,
  ChevronRight,
  LayoutDashboard,
  ListTodo,
  Check,
  File,
  Download,
  Eye,
  Trash2,
} from "lucide-react";
import {
  Snackbar,
  Alert as MuiAlert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import {
  AddRounded,
  FileDownloadRounded,
  MoreVertRounded,
  Settings,
  Close,
  WarningAmber,
  DeleteOutline,
} from "@mui/icons-material";
import { useTheme } from "../ThemeContext";
import { usePermissions } from "../permissions/PermissionsProvider";
import { ACTIONS } from "../permissions/definition";
import { HeaderActionsContext } from "./Layout";
import {
  createInvoiceForProject,
  exportProjectsToExcel,
} from "../services/projectActions";
import {
  fetchConversionRate,
  fetchCBUAEConversionRate,
} from "../utils/invoiceGenerator";
import {
  deriveInvoiceRaised,
  evaluateProjectStatuses,
  isInvoiceDateSet,
} from "../utils/status";
import { buildTasksSearchText } from "../utils/tasksSearchText";
import EditProject from "./EditProject";
import AddProject from "./AddProject";
import AddClient from "./AddClient";
import DeleteProjectDialog from "./orders/DeleteProjectDialog";
import InvoiceDialog from "./orders/InvoiceDialog";
import SettingsDialog from "./SettingsDialog";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import PDFPreview from "../good-UI-example/PDFPreview";
import ProjectSummaryDialog from "./orders/ProjectSummaryDialog";
import { deleteObject, ref } from "firebase/storage";
import { buildProjectSummary } from "../utils/projectSummary";
import { showCloudSaveToast } from "./CloudSaveToast";
import {
  getAllowedBillingEntitiesForProjects,
  resolveBillingEntitySelection,
} from "../utils/billingEntity";
import { createNoBackdropCloseHandler } from "../utils/dialogClose";

// Custom Alert Component
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// Quill Modules
const modules = {
  toolbar: [
    [{ "font": [] }],
    [{ "header": [1, 2, 3, 4, 5, 6, false] }],
    [{ "align": [] }],
    ["bold", "italic", "underline", "strike"],
    [{ "color": [] }, { "background": [] }],
    [{ "list": "ordered" }, { "list": "bullet" }],
    ["blockquote", "code-block"],
    ["link", "image"],
    ["clean"],
  ],
};

const formats = [
  "font",
  "header",
  "align",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "list",
  "bullet",
  "blockquote",
  "code-block",
  "link",
  "image",
];

const CircleIconButton = ({
  icon,
  tooltipId,
  tooltipContent,
  className = "",
  disabled,
  onClick,
}) => {
  return (
    <div className="relative flex justify-center">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        data-tooltip-id={tooltipId}
        className={`w-12 h-12 rounded-full flex items-center justify-center shadow-md transition-all duration-300 ${disabled ? "opacity-50 cursor-not-allowed" : "hover:scale-105"
          } ${className}`}
      >
        {icon}
      </button>
      <ReactTooltip
        id={tooltipId}
        place="bottom"
        effect="solid"
        className="tooltip-centered"
      >
        {tooltipContent}
      </ReactTooltip>
    </div>
  );
};

const circleFlagCodeMap = {
  "UK": "gb",
  "UNITED KINGDOM": "gb",
  "USA": "us",
  "UNITED STATES": "us",
  "GERMANY": "de",
  "FRANCE": "fr",
  "JAPAN": "jp",
  "CHINA": "cn",
  "INDIA": "in",
  "BRAZIL": "br",
  "CANADA": "ca",
  "AUSTRALIA": "au",
  "ITALY": "it",
  "SPAIN": "es",
  "RUSSIA": "ru",
  "SOUTH KOREA": "kr",
  "NETHERLANDS": "nl",
  "TURKEY": "tr",
  "SAUDI ARABIA": "sa",
  "SWITZERLAND": "ch",
  "SWEDEN": "se",
  "POLAND": "pl",
  "BELGIUM": "be",
  "NORWAY": "no",
  "AUSTRIA": "at",
  "DENMARK": "dk",
  "FINLAND": "fi",
  "IRELAND": "ie",
  "NEW ZEALAND": "nz",
  "SINGAPORE": "sg",
  "UAE": "ae",
  "UNITED ARAB EMIRATES": "ae",
  "UAE - ABU DHABI": "ae",
  "UAE - AJMAN": "ae",
  "UAE - DUBAI": "ae",
  "UAE - FUJAIRAH": "ae",
  "UAE - RAK": "ae",
  "UAE - SHARJAH": "ae",
  "UAE - UAQ": "ae",
  "ISRAEL": "il",
  "SOUTH AFRICA": "za",
  "MEXICO": "mx",
  "EGYPT": "eg",
  "MOROCCO": "ma",
  "ALGERIA": "dz",
  "TUNISIA": "tn",
  "LIBYA": "ly",
  "QATAR": "qa",
  "KUWAIT": "kw",
  "BAHRAIN": "bh",
  "OMAN": "om",
  "JORDAN": "jo",
  "LEBANON": "lb",
  "IRAQ": "iq",
  "YEMEN": "ye",
  "SYRIA": "sy",
  "PALESTINE": "ps",
  "IRAN": "ir",
  "PAKISTAN": "pk",
  "AFGHANISTAN": "af",
  "DJIBOUTI": "dj",
  "MAURITANIA": "mr",
  "SOMALIA": "so",
  "SUDAN": "sd",
};

const getCircleFlagCode = (countryOrCompany) => {
  if (!countryOrCompany) return "xx";
  const normalized = countryOrCompany.trim().toUpperCase();

  if (/^[A-Z]{2}$/.test(normalized)) {
    return normalized.toLowerCase();
  }

  return circleFlagCodeMap[normalized] || "xx";
};

const getCircleFlagUrl = (countryOrCompany) =>
  `https://hatscripts.github.io/circle-flags/flags/${getCircleFlagCode(
    countryOrCompany
  )}.svg`;

const CountryFlagIcon = ({ country, size = 22, className = "" }) => (
  <img
    src={getCircleFlagUrl(country)}
    alt={country ? `${country} flag` : "Flag"}
    width={size}
    height={size}
    className={`flex-shrink-0 ${className}`}
    style={{
      borderRadius: "50%",
      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.12)",
      border: "1px solid rgba(15, 23, 42, 0.08)",
      backgroundColor: "#fff",
    }}
    loading="lazy"
  />
);

const ProjectDetail = ({ user }) => {
  const { id } = useParams();
  const { theme } = useTheme();
  const { setHeaderActions } = useContext(HeaderActionsContext);
  const [project, setProject] = useState(null);
  const [briefing, setBriefing] = useState("");
  const [open, setOpen] = useState(false);
  const [editingCompletedOn, setEditingCompletedOn] = useState({
    taskNo: null,
    value: "",
  });
  const [activeTab, setActiveTab] = useState("tasks"); // 'tasks' | 'briefing' | 'documents'

  // Actions menu state
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openAddClient, setOpenAddClient] = useState(false);
  const [isInvoiceDialogOpen, setIsInvoiceDialogOpen] = useState(false);
  const [invoiceChoice, setInvoiceChoice] = useState("Show subject");
  const [selectedCompany, setSelectedCompany] = useState("UK");
  const [useManualAED, setUseManualAED] = useState(false);
  const [showGBP, setShowGBP] = useState(true);
  const [exchangeRateSource, setExchangeRateSource] =
    useState("ExchangeRate-API");
  const [exchangeRateAPIRate, setExchangeRateAPIRate] = useState(null);
  const [cbuaeRate, setCbuaeRate] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [pdfPreview, setPdfPreview] = useState({
    isOpen: false,
    url: "",
    filename: "",
  });
  const [isSummaryDialogOpen, setIsSummaryDialogOpen] = useState(false);
  const [summaryPlainText, setSummaryPlainText] = useState("");
  const [summaryHtmlText, setSummaryHtmlText] = useState("");
  const [summaryFormat, setSummaryFormat] = useState("html");
  const [summaryCurrency, setSummaryCurrency] = useState("GBP");
  const [summaryContent, setSummaryContent] = useState({
    GBP: { plainText: "", htmlText: "" },
    AED: { plainText: "", htmlText: "" },
    GBP_AED: { plainText: "", htmlText: "" },
  });
  const [hasCopiedSummary, setHasCopiedSummary] = useState(false);
  const [summaryStageText, setSummaryStageText] = useState("");
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const [deleteFileDialog, setDeleteFileDialog] = useState({
    open: false,
    target: null,
  });
  const [isDeletingFile, setIsDeletingFile] = useState(false);
  const [deleteFileError, setDeleteFileError] = useState("");
  const [showDateWarningDialog, setShowDateWarningDialog] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("idle"); // idle | saving | success | error
  const [uploadError, setUploadError] = useState("");
  const [pendingUploads, setPendingUploads] = useState(0);
  const uploadClearTimer = useRef(null);
  const pendingUploadsRef = useRef(0);
  const invoiceAllowedCompanies = useMemo(
    () => getAllowedBillingEntitiesForProjects(project ? [project] : []),
    [project]
  );

  // Refs for outside click detection
  const moreMenuRef = useRef(null);
  const addMenuRef = useRef(null);
  const summaryTimersRef = useRef([]);

  useEffect(() => {
    const projectDocRef = doc(db, "projects", id);
    const unsubscribe = onSnapshot(projectDocRef, (snapshot) => {
      if (snapshot.exists()) {
        const projectData = snapshot.data();
        setProject(projectData);
        setBriefing(projectData.projectBriefing);
      }
    });

    return () => unsubscribe();
  }, [id]);

  useEffect(() => {
    return () => {
      if (uploadClearTimer.current) {
        clearTimeout(uploadClearTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    if (pendingUploads <= 0) return undefined;
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue =
        "Your invoice is still saving to the cloud. Are you sure you want to leave?";
      return event.returnValue;
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [pendingUploads]);

  const handleBriefingChange = (value) => {
    setBriefing(value);
  };

  const handleSave = async () => {
    const projectDoc = doc(db, "projects", id);
    await updateDoc(projectDoc, { projectBriefing: briefing });
    setOpen(true);
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpen(false);
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "-";
    const date =
      typeof dateValue?.toDate === "function"
        ? dateValue.toDate()
        : dateValue?.seconds
          ? new Date(dateValue.seconds * 1000)
          : new Date(dateValue);
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const derivedInvoiceRaised = useMemo(
    () => deriveInvoiceRaised(project?.dateForInvoice, project?.invoiceRaised),
    [project?.dateForInvoice, project?.invoiceRaised]
  );

  const handleEditCompletedOn = (task) => {
    setEditingCompletedOn({ taskNo: task.taskNo, value: task.completedOn });
  };

  const handleCompletedOnInputChange = (event) => {
    setEditingCompletedOn({ ...editingCompletedOn, value: event.target.value });
  };

  const fetchAndRefreshTask = async (taskNo) => {
    const projectDocRef = doc(db, "projects", id);
    const projectDoc = await getDoc(projectDocRef);

    if (!projectDoc.exists()) return;

    const projectData = projectDoc.data();
    if (!projectData.tasks) return;

    const {
      updatedTasks,
      projectStatus,
      invoiceRaised: invoiceRaisedDerived,
      paymentStatus,
    } = evaluateProjectStatuses(projectData);
    const originalTasks = projectData.tasks || [];
    const tasksChanged =
      originalTasks.length !== updatedTasks.length ||
      updatedTasks.some(
        (task, index) =>
          task.taskStatus !==
          (originalTasks[index] && originalTasks[index].taskStatus)
      );

    const updates = {};
    if (tasksChanged) {
      updates.tasks = updatedTasks;
      updates.tasksSearchText = buildTasksSearchText(updatedTasks);
    }
    if (projectData.projectStatus !== projectStatus) {
      updates.projectStatus = projectStatus;
    }
    if (projectData.invoiceRaised !== invoiceRaisedDerived) {
      updates.invoiceRaised = invoiceRaisedDerived;
    }
    if (projectData.paymentStatus !== paymentStatus) {
      updates.paymentStatus = paymentStatus || null;
    }

    if (Object.keys(updates).length > 0) {
      await updateDoc(projectDocRef, updates);
    }

    setProject({
      ...projectData,
      tasks: updatedTasks,
      projectStatus,
      invoiceRaised: invoiceRaisedDerived,
      paymentStatus,
    });
  };

  const handleCompletedOnInputBlur = async (task) => {
    const updatedTasks = project.tasks.map((t) =>
      t.taskNo === task.taskNo
        ? { ...t, completedOn: editingCompletedOn.value }
        : t
    );
    const projectDocRef = doc(db, "projects", id);
    await updateDoc(projectDocRef, {
      tasks: updatedTasks,
      tasksSearchText: buildTasksSearchText(updatedTasks),
    });
    setProject({ ...project, tasks: updatedTasks });
    setEditingCompletedOn({ taskNo: null, value: "" });
    fetchAndRefreshTask(task.taskNo);
  };

  const calculateProgress = () => {
    if (!project?.tasks || project.tasks.length === 0) return 0;
    const completed = project.tasks.filter((t) => t.completedOn).length;
    return Math.round((completed / project.tasks.length) * 100);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "bg-green-100 text-green-800 border-green-200";
      case "Overdue":
        return "bg-red-100 text-red-800 border-red-200";
      case "Attention":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "In Progress":
        return "bg-blue-100 text-blue-800 border-blue-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Completed":
        return <CheckCircle className="w-4 h-4 mr-1" />;
      case "Overdue":
        return <AlertCircle className="w-4 h-4 mr-1" />;
      case "Attention":
        return <Clock className="w-4 h-4 mr-1" />;
      case "In Progress":
        return <Activity className="w-4 h-4 mr-1" />;
      default:
        return <Activity className="w-4 h-4 mr-1" />;
    }
  };

  // Timeline-specific helpers
  const getTimelineNodeColor = (status) => {
    switch (status) {
      case "Completed":
        return "bg-green-500";
      case "Overdue":
        return "bg-red-500";
      case "Attention":
        return "bg-yellow-500";
      case "In Progress":
        return "bg-blue-500";
      default:
        return "bg-gray-500";
    }
  };

  const getTimelineIcon = (status) => {
    switch (status) {
      case "Completed":
        return <CheckCircle className="w-5 h-5 text-white" />;
      case "Overdue":
        return <AlertCircle className="w-5 h-5 text-white" />;
      case "Attention":
        return <Clock className="w-5 h-5 text-white" />;
      case "In Progress":
        return <Activity className="w-5 h-5 text-white" />;
      default:
        return <Activity className="w-5 h-5 text-white" />;
    }
  };

  const { can } = usePermissions();
  const canViewFinancials = can(ACTIONS.FINANCIALS_VIEW);
  const canManageProject = can(ACTIONS.PROJECTS_EDIT);
  const canCreateProject = can(ACTIONS.PROJECTS_CREATE);
  const canCreateInvoice = can(ACTIONS.INVOICES_CREATE);
  const canDelete = can(ACTIONS.PROJECTS_DELETE);
  const canExport = can(ACTIONS.PROJECTS_EXPORT);
  const canManageSettings = can(ACTIONS.SETTINGS_MANAGE_PERMISSIONS);
  const canAddClient = can(ACTIONS.CLIENTS_EDIT);
  const canGenerateSummary = can(ACTIONS.FINANCIALS_VIEW);

  useEffect(() => {
    if (selectedCompany === "UK") {
      setUseManualAED(false);
      setShowGBP(true);
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (selectedCompany === "UAE" && isInvoiceDialogOpen) {
      fetchBothRates();
    }
  }, [selectedCompany, isInvoiceDialogOpen]);

  const fetchBothRates = async () => {
    setLoadingRates(true);
    try {
      const [apiRate, cbuae] = await Promise.all([
        fetchConversionRate("GBP", "AED"),
        fetchCBUAEConversionRate(),
      ]);
      setExchangeRateAPIRate(apiRate);
      setCbuaeRate(cbuae);
    } catch (error) {
      console.error("Error fetching rates:", error);
    } finally {
      setLoadingRates(false);
    }
  };

  // Outside click handler for menus
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (addMenuRef.current && !addMenuRef.current.contains(event.target)) {
        setShowAddMenu(false);
      }
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setShowMoreMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Action handlers
  const handleOpenAdd = () => setOpenAdd(true);
  const handleCloseAdd = () => setOpenAdd(false);
  const handleOpenAddClient = () => setOpenAddClient(true);
  const handleCloseAddClient = () => setOpenAddClient(false);
  const handleCloseEdit = () => {
    setShowEditModal(false);
  };

  const handleOpenDeleteDialog = () => {
    setDeletePassword("");
    setDeleteError("");
    setShowDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setShowDeleteDialog(false);
    setDeleteError("");
  };

  const handleDeleteConfirm = async () => {
    if (deletePassword !== "Superuser") {
      setDeleteError("Incorrect password");
      return;
    }

    try {
      await deleteDoc(doc(db, "projects", id));
      handleCloseDeleteDialog();
      window.location.href = "/orders";
    } catch (error) {
      console.error("Error deleting project:", error);
      setDeleteError("Failed to delete project");
    }
  };

  const handleExport = async () => {
    if (project) {
      await exportProjectsToExcel([id]);
    }
  };

  const normalizeDateValue = useCallback((value) => {
    if (!value) return null;
    if (typeof value?.toDate === "function") {
      return value.toDate();
    }
    if (value?.seconds) {
      return new Date(value.seconds * 1000);
    }
    const asDate = new Date(value);
    return Number.isNaN(asDate.getTime()) ? null : asDate;
  }, []);

  const summarizeProject = useCallback(
    (proj, currency = "GBP") =>
      buildProjectSummary(proj, { normalizeDateValue, currency }),
    [normalizeDateValue]
  );

  const formatMoney = (value) => {
    if (typeof value !== "number") {
      value = parseFloat(value);
    }
    if (isNaN(value)) {
      return "£0";
    }
    return `£${value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  };

  const formatMoneyWithoutPoundSign = (value) => {
    if (typeof value !== "number") {
      value = parseFloat(value);
    }
    if (isNaN(value)) {
      return "£0";
    }
    return `${value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  };

  const formatFileSize = (size) => {
    if (size === undefined || size === null || isNaN(Number(size))) return "";
    const bytes = Number(size);
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const isPdfFile = (file) => {
    const name = (file?.name || file?.filename || "").toLowerCase();
    const contentType = file?.contentType || "";
    const url = (file?.url || "").toLowerCase();
    return (
      contentType.includes("pdf") ||
      name.endsWith(".pdf") ||
      url.includes(".pdf")
    );
  };

  const openPdfPreview = (url, filename) => {
    if (!url) return;
    setPdfPreview({
      isOpen: true,
      url,
      filename: filename || "Document",
    });
  };

  const closePdfPreview = () => {
    setPdfPreview({ isOpen: false, url: "", filename: "" });
  };

  const handleViewFile = (file) => {
    if (!file?.url) return;
    const filename = file.name || file.filename || "Document";
    if (isPdfFile(file)) {
      openPdfPreview(file.url, filename);
    } else {
      window.open(file.url, "_blank", "noopener,noreferrer");
    }
  };

  const handleDownloadFile = async (e, fileUrl, filename = "document.pdf") => {
    e?.preventDefault();
    if (!fileUrl) return;
    try {
      const res = await fetch(fileUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      const normalizedName = (filename || "document").toString();
      const safeName = normalizedName.toLowerCase().endsWith(".pdf")
        ? normalizedName
        : `${normalizedName}.pdf`;
      link.href = blobUrl;
      link.download = safeName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const openDeleteAttachmentDialog = (type, item, index) => {
    setDeleteFileError("");
    setDeleteFileDialog({
      open: true,
      target: { type, item, index },
    });
  };

  const closeDeleteAttachmentDialog = () => {
    setDeleteFileDialog({ open: false, target: null });
    setDeleteFileError("");
  };

  const handleConfirmDeleteAttachment = async () => {
    if (!deleteFileDialog.target || !project) return;
    const { type, item, index } = deleteFileDialog.target;

    setIsDeletingFile(true);
    setDeleteFileError("");

    try {
      const projectRef = doc(db, "projects", id);
      const updates = {};

      if (type === "document") {
        const filteredDocs = (project.documents || []).filter(
          (_, idx) => idx !== index
        );
        updates.documents = filteredDocs;
        setProject((prev) =>
          prev ? { ...prev, documents: filteredDocs } : prev
        );
      } else if (type === "invoice") {
        const filteredInvoices = (project.invoices || []).filter(
          (_, idx) => idx !== index
        );
        updates.invoices = filteredInvoices;
        setProject((prev) =>
          prev ? { ...prev, invoices: filteredInvoices } : prev
        );
      }

      if (Object.keys(updates).length === 0) {
        setIsDeletingFile(false);
        closeDeleteAttachmentDialog();
        return;
      }

      await updateDoc(projectRef, updates);

      if (item?.path) {
        try {
          await deleteObject(ref(storage, item.path));
        } catch (err) {
          console.error("Failed to delete file from storage:", err);
        }
      }

      closeDeleteAttachmentDialog();
    } catch (error) {
      console.error("Error deleting attachment:", error);
      setDeleteFileError("Unable to delete. Please try again.");
    } finally {
      setIsDeletingFile(false);
    }
  };

  const handleCreateInvoice = async () => {
    if (!project) return;
    const nextSelectedCompany = resolveBillingEntitySelection(
      [project],
      selectedCompany
    );

    if (!nextSelectedCompany) {
      alert(
        "This project has a conflicting billing entity setup. Update the project company before invoicing."
      );
      return;
    }

    setSelectedCompany(nextSelectedCompany);
    setShowMoreMenu(false);
    setIsInvoiceDialogOpen(true);
  };

  const handleInvoiceCreation = async () => {
    if (!id || isGeneratingInvoice) return;

    if (project && !isInvoiceDateSet(project.dateForInvoice)) {
      setShowDateWarningDialog(true);
      return;
    }

    await proceedWithInvoiceCreation();
  };

  const handleUploadTracking = useCallback((uploadPromise) => {
    if (!uploadPromise || typeof uploadPromise.then !== "function") return;
    setUploadError("");
    setUploadStatus("saving");
    setPendingUploads((count) => {
      const next = count + 1;
      pendingUploadsRef.current = next;
      showCloudSaveToast({
        status: "saving",
        pendingCount: next,
      });
      return next;
    });

    uploadPromise
      .then(() => {
        const stillPending = pendingUploadsRef.current > 1;
        setUploadStatus(stillPending ? "saving" : "success");
        showCloudSaveToast({
          status: stillPending ? "saving" : "success",
          pendingCount: pendingUploadsRef.current,
        });
        if (uploadClearTimer.current) {
          clearTimeout(uploadClearTimer.current);
        }
        uploadClearTimer.current = setTimeout(() => {
          setUploadStatus((status) => (status === "success" ? "idle" : status));
          setUploadError("");
        }, 2200);
      })
      .catch((error) => {
        console.error("Error saving invoice to cloud:", error);
        const message =
          error?.message || "Failed to save invoice to the cloud.";
        setUploadError(message);
        setUploadStatus("error");
        showCloudSaveToast({
          status: "error",
          errorMessage: message,
          pendingCount: pendingUploadsRef.current,
        });
      })
      .finally(() => {
        setPendingUploads((count) => {
          const next = Math.max(0, count - 1);
          pendingUploadsRef.current = next;
          return next;
        });
      });
  }, []);

  const proceedWithInvoiceCreation = async () => {
    if (!id) return;

    setShowDateWarningDialog(false);
    setIsGeneratingInvoice(true);
    try {
      const result = await createInvoiceForProject({
        projectId: id,
        invoiceChoice,
        selectedCompany,
        formatMoney,
        formatMoneyWithoutPoundSign,
        useManualAED,
        showGBP,
        exchangeRateSource,
      });
      if (result?.uploadPromise) {
        handleUploadTracking(result.uploadPromise);
      }
      setIsInvoiceDialogOpen(false);
    } catch (error) {
      console.error("Error generating invoice:", error);
    } finally {
      setIsGeneratingInvoice(false);
    }
  };

  const handleOpenSummaryDialog = useCallback(() => {
    if (!project) return;
    const gbpSummary = summarizeProject(project, "GBP");
    const aedSummary = summarizeProject(project, "AED");
    const bothSummary = summarizeProject(project, "GBP_AED");
    setSummaryContent({
      GBP: gbpSummary,
      AED: aedSummary,
      GBP_AED: bothSummary,
    });
    setSummaryCurrency("GBP");
    setSummaryPlainText(gbpSummary.plainText);
    setSummaryHtmlText(gbpSummary.htmlText);
    setSummaryFormat("html");
    setHasCopiedSummary(false);
    setIsSummaryDialogOpen(true);

    // Clear any existing timers
    summaryTimersRef.current.forEach((t) => clearTimeout(t));
    summaryTimersRef.current = [];
    setIsSummaryLoading(true);

    const stages = [
      { label: "Accessing data...", duration: 400 },
      { label: "Assembling information...", duration: 850 },
      { label: "Preparing draft...", duration: 600 },
      { label: "Drafting summary...", duration: 850 },
      { label: "Done", duration: 500 },
    ];

    let elapsed = 0;
    stages.forEach((stage, idx) => {
      const timer = setTimeout(() => {
        setSummaryStageText(stage.label);
        if (idx === stages.length - 1) {
          setIsSummaryLoading(false);
        }
      }, elapsed);
      summaryTimersRef.current.push(timer);
      elapsed += stage.duration;
    });
  }, [project, summarizeProject]);

  const handleCloseSummaryDialog = useCallback(() => {
    setIsSummaryDialogOpen(false);
    summaryTimersRef.current.forEach((t) => clearTimeout(t));
    summaryTimersRef.current = [];
    setIsSummaryLoading(false);
  }, []);

  const handleSummaryFormatChange = useCallback((format) => {
    setSummaryFormat(format);
    setHasCopiedSummary(false);
  }, []);

  const handleSummaryCurrencyChange = useCallback(
    (currency) => {
      const key = (currency || "GBP").toUpperCase();
      const content = summaryContent[key] ||
        summaryContent.GBP || { plainText: "", htmlText: "" };
      setSummaryCurrency(key);
      setSummaryPlainText(content.plainText || "");
      setSummaryHtmlText(content.htmlText || "");
      setHasCopiedSummary(false);
    },
    [summaryContent]
  );

  const handleCopySummary = useCallback(() => {
    const markCopied = () => {
      setHasCopiedSummary(true);
      setTimeout(() => setHasCopiedSummary(false), 2000);
    };

    if (summaryFormat === "html") {
      const html = summaryHtmlText;
      const plain = summaryPlainText;
      if (!html && !plain) return;

      const fallback = () => {
        const textarea = document.createElement("textarea");
        textarea.value = html || plain;
        textarea.style.position = "fixed";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand("copy");
          markCopied();
        } catch (err) {
          console.error("Failed to copy formatted summary", err);
        } finally {
          document.body.removeChild(textarea);
        }
      };

      if (navigator?.clipboard?.write) {
        const items = [
          new ClipboardItem({
            "text/html": new Blob([html || ""], { type: "text/html" }),
            "text/plain": new Blob([plain || ""], { type: "text/plain" }),
          }),
        ];
        navigator.clipboard
          .write(items)
          .then(() => markCopied())
          .catch((err) => {
            console.error("Failed to copy formatted summary", err);
            fallback();
          });
      } else {
        fallback();
      }
      return;
    }

    const textToCopy = summaryPlainText;
    if (!textToCopy) return;

    const fallbackCopy = () => {
      const textarea = document.createElement("textarea");
      textarea.value = textToCopy;
      textarea.style.position = "fixed";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand("copy");
        markCopied();
      } catch (err) {
        console.error("Failed to copy summary", err);
      } finally {
        document.body.removeChild(textarea);
      }
    };

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard
        .writeText(textToCopy)
        .then(() => markCopied())
        .catch((err) => {
          console.error("Failed to copy summary", err);
          fallbackCopy();
        });
    } else {
      fallbackCopy();
    }
  }, [summaryFormat, summaryHtmlText, summaryPlainText]);

  // Build actions node for header
  const actionsNode = useMemo(() => {
    const hasAddActions = canCreateProject || canAddClient;
    const hasMoreActions =
      canManageProject || canCreateInvoice || canDelete || canGenerateSummary;
    const hasToolbar =
      project &&
      (hasAddActions || hasMoreActions || canExport || canManageSettings);

    if (!hasToolbar) return null;

    return (
      <div className="flex items-center gap-4">
        {/* Add Menu */}
        {hasAddActions && (
          <div className="relative" ref={addMenuRef}>
            <CircleIconButton
              onClick={() => {
                setShowAddMenu((s) => !s);
                setShowMoreMenu(false);
              }}
              disabled={!hasAddActions}
              tooltipId="add-menu-tooltip"
              tooltipContent="Add..."
              className={theme.toolbarAddButton}
              icon={<AddRounded sx={{ fontSize: 30 }} aria-hidden="true" />}
            />
            {showAddMenu && (
              <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-56 max-w-[calc(100vw-40px)] rounded-xl border border-gray-200 bg-white shadow-lg z-20">
                <button
                  type="button"
                  onClick={() => {
                    if (!canCreateProject) return;
                    setShowAddMenu(false);
                    handleOpenAdd();
                  }}
                  disabled={!canCreateProject}
                  className={`w-full text-left px-4 py-3 flex items-start gap-3 rounded-t-xl ${!canCreateProject
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-slate-50"
                    }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-7 h-7 text-black mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 12v6m3-3h-6"
                    />
                  </svg>
                  <div>
                    <div className="text-sm font-medium text-gray-800">
                      Add Project
                    </div>
                    <div className="text-xs text-gray-500">
                      Create a new project
                    </div>
                  </div>
                </button>
                <div className="h-px bg-gray-100" />
                <button
                  type="button"
                  onClick={() => {
                    if (!canAddClient) return;
                    setShowAddMenu(false);
                    handleOpenAddClient();
                  }}
                  disabled={!canAddClient}
                  className={`w-full text-left px-4 py-3 flex items-start gap-3 rounded-b-xl ${!canAddClient
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-slate-50"
                    }`}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-7 h-7 text-black mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z"
                    />
                  </svg>
                  <div>
                    <div className="text-sm font-medium text-gray-800">
                      Add Client
                    </div>
                    <div className="text-xs text-gray-500">
                      Create a new client
                    </div>
                  </div>
                </button>
              </div>
            )}
          </div>
        )}

        {/* Export Button */}
        <CircleIconButton
          onClick={handleExport}
          disabled={!canExport}
          tooltipId="export-tooltip"
          tooltipContent="Export to Excel"
          className={theme.toolbarExportButton}
          icon={
            <FileDownloadRounded sx={{ fontSize: 30 }} aria-hidden="true" />
          }
        />

        {/* Settings Button */}
        {canManageSettings && (
          <CircleIconButton
            onClick={() => setShowSettings(true)}
            tooltipId="settings-tooltip"
            tooltipContent="Settings"
            className="bg-gray-200 text-gray-700 hover:bg-gray-300"
            icon={<Settings sx={{ fontSize: 24 }} aria-hidden="true" />}
          />
        )}

        {/* More Menu */}
        {hasMoreActions && (
          <div className="relative" ref={moreMenuRef}>
            <CircleIconButton
              onClick={() => {
                setShowMoreMenu((s) => !s);
                setShowAddMenu(false);
              }}
              disabled={!hasMoreActions}
              tooltipId="more-menu-tooltip"
              tooltipContent="More actions"
              className="bg-gray-200 text-gray-700 hover:bg-gray-300"
              icon={
                <MoreVertRounded sx={{ fontSize: 28 }} aria-hidden="true" />
              }
            />
            {showMoreMenu && (
              <div className="absolute left-1/2 -translate-x-1/2 mt-2 w-56 max-w-[calc(100vw-40px)] rounded-xl border border-gray-200 bg-white shadow-lg z-20 overflow-hidden">
                {canManageProject && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowMoreMenu(false);
                      setShowEditModal(true);
                    }}
                    className="w-full text-left px-4 py-3 flex items-start gap-3 rounded-t-xl hover:bg-gray-100"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-7 h-7 text-black mt-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
                      />
                    </svg>
                    <div>
                      <div className="text-sm font-medium text-gray-800">
                        Edit
                      </div>
                      <div className="text-xs text-gray-500">
                        Edit this project
                      </div>
                    </div>
                  </button>
                )}
                {canCreateInvoice && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowMoreMenu(false);
                      handleCreateInvoice();
                    }}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-100 ${canDelete || canGenerateSummary ? "" : "rounded-b-xl"
                      }`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-7 h-7 text-black mt-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z"
                      />
                    </svg>
                    <div>
                      <div className="text-sm font-medium text-gray-800">
                        Create Invoice
                      </div>
                      <div className="text-xs text-gray-500">
                        For this project
                      </div>
                    </div>
                  </button>
                )}
                {canGenerateSummary && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowMoreMenu(false);
                      handleOpenSummaryDialog();
                    }}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-100 ${canDelete ? "" : "rounded-b-xl"
                      }`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-7 h-7 text-black mt-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M7 4h7l4 4v10a2 2 0 01-2 2H7a2 2 0 01-2-2V6a2 2 0 012-2z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M14 4v4h4"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M9 12h6M9 16h3"
                      />
                    </svg>
                    <div>
                      <div className="text-sm font-medium text-gray-800">
                        Generate Summary
                      </div>
                      <div className="text-xs text-gray-500">
                        Email-ready overview
                      </div>
                    </div>
                  </button>
                )}
                {canDelete && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowMoreMenu(false);
                      handleOpenDeleteDialog();
                    }}
                    className="w-full text-left px-4 py-3 flex items-start gap-3 rounded-b-xl hover:bg-red-50"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-7 h-7 text-black mt-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                      />
                    </svg>
                    <div>
                      <div className="text-sm font-medium text-gray-800">
                        Delete Project
                      </div>
                      <div className="text-xs text-gray-500">
                        Remove project permanently
                      </div>
                    </div>
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        <SettingsDialog
          open={showSettings}
          onClose={() => setShowSettings(false)}
        />
      </div>
    );
  }, [
    canAddClient,
    canCreateInvoice,
    canCreateProject,
    canDelete,
    canExport,
    canGenerateSummary,
    canManageProject,
    canManageSettings,
    project,
    showAddMenu,
    showMoreMenu,
    showSettings,
    theme,
    handleOpenSummaryDialog,
  ]);

  // Inject actions into header
  useEffect(() => {
    setHeaderActions(actionsNode);
    return () => setHeaderActions(null);
  }, [actionsNode, setHeaderActions]);

  if (!project) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const progress = calculateProgress();
  const documents = project.documents || [];
  const sortedInvoices = canViewFinancials
    ? [...(project.invoices || [])].sort((a, b) => {
      const getTime = (value) => {
        if (!value) return 0;
        if (typeof value?.toDate === "function")
          return value.toDate().getTime();
        if (value?.seconds) return value.seconds * 1000;
        const time = new Date(value).getTime();
        return isNaN(time) ? 0 : time;
      };
      return getTime(b.createdAt) - getTime(a.createdAt);
    })
    : [];
  const visibleAttachmentCount = documents.length + sortedInvoices.length;
  const deleteTargetType =
    deleteFileDialog.target?.type === "invoice" ? "invoice" : "document";
  const deleteTargetName =
    deleteFileDialog.target?.item?.name ||
    deleteFileDialog.target?.item?.filename ||
    deleteFileDialog.target?.item?.invoiceNo ||
    deleteFileDialog.target?.item?.projectRef ||
    deleteFileDialog.target?.item?.url ||
    "";

  return (
    <div className="min-h-screen bg-gray-50 pb-12 pt-6 font-sans">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div
          className={`rounded-2xl shadow-lg border border-gray-200 p-6 mb-8 relative overflow-hidden ${theme.formHeaderBg || "bg-white"
            }`}
        >
          {/* Background decoration */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full mix-blend-overlay filter blur-3xl opacity-20 -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full mix-blend-overlay filter blur-3xl opacity-20 translate-y-1/2 -translate-x-1/2"></div>

          <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div className="text-white">
              <div className="flex items-center gap-3 mb-2">
                {/* Company Flag from HatScripts circle-flags CDN */}
                <img
                  src={getCircleFlagUrl(project.company)}
                  alt={project.company ? `${project.company} flag` : "Flag"}
                  width={32}
                  height={32}
                  style={{
                    flexShrink: 0,
                    borderRadius: "50%",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    border: "2px solid rgba(255,255,255,0.3)",
                  }}
                />
                <h1 className="text-3xl font-bold tracking-tight">
                  {project.projectRef}
                </h1>
                <span
                  className="px-3 py-1 rounded-full text-sm font-semibold border bg-white/10 border-white/20 backdrop-blur-sm"
                  style={{
                    color:
                      project.projectStatus === "Overdue"
                        ? "#ff8a8a"
                        : project.projectStatus === "Attention"
                          ? "#ffd43b"
                          : project.projectStatus === "In Progress"
                            ? "#69db7c"
                            : project.projectStatus === "Paid"
                              ? "#34d399"
                            : project.projectStatus === "Completed"
                              ? "#a5d8ff"
                              : project.projectStatus === "To be invoiced"
                                ? "#ffa8d4"
                                : project.projectStatus === "Invoiced"
                                  ? "#d0bfff"
                                  : "#ffffff",
                  }}
                >
                  {project.projectStatus}
                </span>
              </div>
              <p className="text-blue-100 flex items-center gap-2">
                <Briefcase className="w-4 h-4" />
                Project Details & Management
              </p>
            </div>

            <div className="flex items-center gap-6 bg-white/10 backdrop-blur-md p-4 rounded-xl border border-white/20 text-white">
              <div className="text-center">
                <p className="text-sm text-blue-100 font-medium mb-1">
                  Progress
                </p>
                <div className="relative h-16 w-16">
                  <svg
                    className="h-full w-full transform -rotate-90"
                    viewBox="0 0 36 36"
                  >
                    <path
                      className="text-white/20"
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                    />
                    <path
                      className="text-white transition-all duration-1000 ease-out"
                      strokeDasharray={`${progress}, 100`}
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-sm font-bold">{progress}%</span>
                  </div>
                </div>
              </div>
              <div className="h-12 w-px bg-white/20"></div>
              <div className="text-center min-w-[80px]">
                <p className="text-sm text-blue-100 font-medium mb-1">Tasks</p>
                <p className="text-2xl font-bold">
                  {project.tasks?.length || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Two-Column Layout: Sidebar + Content Area */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Sidebar (1/3) */}
          <div className="lg:w-1/3 space-y-6">
            {/* Financial Summary Card - Privileged Users Only */}
            {canViewFinancials && (
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-indigo-600" />
                    Financial Summary
                  </h3>
                  <div className="space-y-4">
                    {/* Cost */}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">
                        Project Cost:
                      </span>
                      <span className="text-sm font-semibold text-gray-900">
                        £{project.cost || "0.00"}
                      </span>
                    </div>

                    {/* Income */}
                    <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                      <span className="text-sm text-gray-600">
                        Total Income:
                      </span>
                      <span className="text-sm font-semibold text-gray-900">
                        £{project.income || "0.00"}
                      </span>
                    </div>

                    {/* VAT */}
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">VAT:</span>
                      <span className="text-sm font-semibold text-gray-900">
                        {project.vat || "0"}%
                      </span>
                    </div>

                    {/* Total with VAT */}
                    <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                      <span className="text-base font-medium text-gray-800">
                        Total Inc VAT:
                      </span>
                      <span className="text-xl font-bold text-indigo-600">
                        £{project.amountIncVat || "0.00"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Project Information Card */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-indigo-600" />
                  Project Information
                </h3>
                <div className="space-y-3">
                  {/* Client */}
                  <div>
                    <span className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Client
                    </span>
                    <span className="block text-sm font-semibold text-gray-900">
                      {project.client || "N/A"}
                    </span>
                  </div>

                  {/* Point of Contact */}
                  <div>
                    <span className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Contact
                    </span>
                    <span className="block text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      {project.poc || "N/A"}
                    </span>
                  </div>

                  {/* Project Manager */}
                  <div>
                    <span className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Manager
                    </span>
                    <span className="block text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      {project.projectManager || "N/A"}
                    </span>
                  </div>

                  {/* Created Date */}
                  <div>
                    <span className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                      Created
                    </span>
                    <span className="block text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      {formatDate(project.createdAt)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Information Card - Privileged Users Only */}
            {canViewFinancials && (
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-indigo-600" />
                    Payment Information
                  </h3>
                  <div className="space-y-3">
                    {/* Invoice Number */}
                    <div>
                      <span className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                        Invoice No
                      </span>
                      <span className="block text-sm font-semibold text-gray-900">
                        {project.invoiceNo || "N/A"}
                      </span>
                    </div>

                    {/* Invoice Raised */}
                    <div>
                      <span className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                        Invoice Raised
                      </span>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${derivedInvoiceRaised === "Yes"
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                          }`}
                      >
                        {derivedInvoiceRaised || "No"}
                      </span>
                    </div>

                    {/* Payment Status */}
                    <div>
                      <span className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                        Payment Status
                      </span>
                      <span className="block text-sm font-semibold text-gray-900">
                        {project.paymentStatus || "N/A"}
                      </span>
                    </div>

                    {/* Payment Date */}
                    <div>
                      <span className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                        Paid On
                      </span>
                      <span className="block text-sm font-semibold text-gray-900">
                        {formatDate(project.paidOn)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Content Area (2/3) */}
          <div className="lg:w-2/3">
            {/* Tabs */}
            <div className="flex gap-6 mb-6 border-b border-gray-200 relative z-10">
              <button
                onClick={() => setActiveTab("tasks")}
                className={`pb-4 px-2 text-sm font-medium transition-colors relative ${activeTab === "tasks"
                  ? "text-indigo-600"
                  : "text-gray-500 hover:text-gray-700"
                  }`}
              >
                <div className="flex items-center gap-2">
                  <ListTodo className="w-4 h-4" />
                  Tasks Timeline
                </div>
                {activeTab === "tasks" && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-t-full"></div>
                )}
              </button>
              <button
                onClick={() => setActiveTab("briefing")}
                className={`pb-4 px-2 text-sm font-medium transition-colors relative ${activeTab === "briefing"
                  ? "text-indigo-600"
                  : "text-gray-500 hover:text-gray-700"
                  }`}
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Project Briefing
                </div>
                {activeTab === "briefing" && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-t-full"></div>
                )}
              </button>
              <button
                onClick={() => setActiveTab("documents")}
                className={`pb-4 px-2 text-sm font-medium transition-colors relative ${activeTab === "documents"
                  ? "text-indigo-600"
                  : "text-gray-500 hover:text-gray-700"
                  }`}
              >
                <div className="flex items-center gap-2">
                  <File className="w-4 h-4" />
                  Documents
                </div>
                {activeTab === "documents" && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-t-full"></div>
                )}
              </button>
            </div>

            {/* Content Area */}
            <div className="transition-all duration-300 ease-in-out">
              {activeTab === "briefing" && (
                <div className="relative rounded-2xl shadow-xl border border-gray-200 overflow-hidden animate-fadeIn">
                  {/* Enhanced Header with Theme Gradient */}
                  <div
                    className={`relative p-6 border-b border-white/30 overflow-hidden ${theme.formHeaderBg ||
                      "bg-gradient-to-r from-slate-700 to-slate-800"
                      }`}
                  >
                    {/* Background decoration */}
                    <div className="absolute top-0 right-0 w-48 h-48 bg-white rounded-full mix-blend-overlay filter blur-3xl opacity-20 -translate-y-1/2 translate-x-1/3"></div>
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full mix-blend-overlay filter blur-3xl opacity-15 translate-y-1/2 -translate-x-1/3"></div>

                    {/* Content */}
                    <div className="relative z-10 flex flex-col sm:flex-row gap-4 sm:gap-0 justify-between items-start sm:items-center">
                      <div className="flex items-center gap-4">
                        {/* Icon container with glass morphism */}
                        <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center backdrop-blur-sm border border-white/20">
                          <FileText className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                            Project Summary
                          </h2>
                          <p
                            className={`text-sm mt-1 ${theme.formSubText || "text-slate-300"
                              }`}
                          >
                            Comprehensive project scope and requirements
                          </p>
                        </div>
                      </div>

                      {/* Enhanced save button */}
                      <button
                        onClick={handleSave}
                        className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-white/10 backdrop-blur-md border border-white/30 text-white hover:bg-white/20 transition-all duration-200 shadow-lg hover:shadow-xl font-medium group"
                      >
                        <Save className="w-4 h-4 group-hover:scale-110 transition-transform" />
                        Save Changes
                      </button>
                    </div>
                  </div>

                  {/* Enhanced Editor Container */}
                  <div className="p-6 bg-gradient-to-br from-gray-50 via-white to-gray-50/50">
                    {/* Editor Status Bar */}
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-0 sm:items-center justify-between mb-4 px-1">
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <Activity className="w-3.5 h-3.5 text-indigo-600" />
                        <span className="font-medium">Rich Text Editor</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="px-2.5 py-1 rounded-md bg-indigo-50 border border-indigo-100">
                          <span className="text-xs font-medium text-indigo-700">
                            {briefing
                              ? `${briefing
                                .replace(/<[^>]*>/g, "")
                                .split(/\s+/)
                                .filter((w) => w.length > 0).length
                              } words`
                              : "0 words"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Enhanced Editor Wrapper */}
                    <div className="relative group">
                      {/* Subtle border glow effect on focus */}
                      <div className="absolute -inset-0.5 bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-pink-500/20 rounded-xl opacity-0 group-focus-within:opacity-100 blur transition-opacity duration-500"></div>

                      {/* Editor Container */}
                      <div className="relative bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden transition-all duration-300 group-focus-within:shadow-xl group-focus-within:ring-2 group-focus-within:ring-indigo-200">
                        <ReactQuill
                          theme="snow"
                          value={briefing}
                          onChange={handleBriefingChange}
                          modules={modules}
                          formats={formats}
                          className="h-[500px] briefing-editor"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "documents" && (
                <div className="relative rounded-2xl shadow-xl border border-gray-200 bg-white overflow-hidden animate-fadeIn">
                  <div className="p-6 border-b border-gray-100 flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">
                        Project Documents
                      </h3>
                      <p className="text-sm text-gray-500">
                        {canViewFinancials
                          ? "Uploaded files and invoices linked to this project."
                          : "Uploaded files linked to this project."}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <File className="w-4 h-4" />
                      <span>
                        {visibleAttachmentCount} item
                        {visibleAttachmentCount === 1 ? "" : "s"}
                      </span>
                    </div>
                  </div>

                  <div className="p-6 space-y-8 bg-gradient-to-br from-gray-50 via-white to-gray-50/60">
                    <section>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="text-md font-semibold text-gray-800">
                            Uploaded Documents
                          </h4>
                          <p className="text-sm text-gray-500">
                            Files attached directly to this project.
                          </p>
                        </div>
                      </div>

                      {documents.length > 0 ? (
                        <div className="space-y-3">
                          {documents.map((doc, index) => {
                            const displayName =
                              doc.name || `Document ${index + 1}`;

                            return (
                              <div
                                key={doc.url || `${displayName}-${index}`}
                                className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-200 shadow-sm"
                              >
                                <div className="h-10 w-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                                  <File className="w-5 h-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-gray-900 truncate">
                                    {displayName}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {formatFileSize(doc.size)}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleViewFile(doc)}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 rounded-lg border border-indigo-100 transition-colors"
                                  >
                                    <Eye className="w-4 h-4" />
                                    Preview
                                  </button>
                                  <button
                                    onClick={(e) =>
                                      handleDownloadFile(
                                        e,
                                        doc.url,
                                        displayName
                                      )
                                    }
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
                                  >
                                    <Download className="w-4 h-4" />
                                    Download
                                  </button>
                                  {canManageProject && (
                                    <button
                                      onClick={() =>
                                        openDeleteAttachmentDialog(
                                          "document",
                                          doc,
                                          index
                                        )
                                      }
                                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded-lg border border-red-100 transition-colors"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                      Delete
                                    </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center bg-white">
                          <File className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                          <p className="text-sm text-gray-500">
                            No documents uploaded yet.
                          </p>
                        </div>
                      )}
                    </section>

                    {canViewFinancials && (
                      <section>
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="text-md font-semibold text-gray-800">
                              Generated Invoices
                            </h4>
                            <p className="text-sm text-gray-500">
                              Invoices created for this project will appear
                              here.
                            </p>
                          </div>
                        </div>

                        {sortedInvoices.length > 0 ? (
                          <div className="space-y-3">
                            {sortedInvoices.map((invoice, index) => {
                              const displayName =
                                invoice.name ||
                                `Invoice ${sortedInvoices.length - index}`;
                              const amountLabel = invoice.amountIncVat
                                ? `Amount inc VAT: ${invoice.amountIncVat}`
                                : "";

                              return (
                                <div
                                  key={
                                    invoice.url ||
                                    invoice.path ||
                                    `${displayName}-${index}`
                                  }
                                  className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-200 shadow-sm"
                                >
                                  <div className="h-10 w-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center">
                                    <FileText className="w-5 h-5" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-gray-900 truncate">
                                      {displayName}
                                    </p>
                                    <p className="text-xs text-gray-500 flex items-center flex-wrap gap-2">
                                      {invoice.selectedCompany && (
                                        <span className="font-medium text-indigo-600">
                                          {invoice.selectedCompany}
                                        </span>
                                      )}
                                      {invoice.createdAt && (
                                        <span>
                                          • {formatDate(invoice.createdAt)}
                                        </span>
                                      )}
                                      {amountLabel && (
                                        <span>• {amountLabel}</span>
                                      )}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() => handleViewFile(invoice)}
                                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 rounded-lg border border-indigo-100 transition-colors"
                                    >
                                      <Eye className="w-4 h-4" />
                                      Preview
                                    </button>
                                    <button
                                      onClick={(e) =>
                                        handleDownloadFile(
                                          e,
                                          invoice.url,
                                          displayName
                                        )
                                      }
                                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-lg border border-gray-200 transition-colors"
                                    >
                                      <Download className="w-4 h-4" />
                                      Download
                                    </button>
                                    {canManageProject && (
                                      <button
                                        onClick={() =>
                                          openDeleteAttachmentDialog(
                                            "invoice",
                                            invoice,
                                            index
                                          )
                                        }
                                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded-lg border border-red-100 transition-colors"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                        Delete
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center bg-white">
                            <FileText className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                            <p className="text-sm text-gray-500">
                              No invoices generated yet.
                            </p>
                          </div>
                        )}
                      </section>
                    )}
                  </div>
                </div>
              )}

              {activeTab === "tasks" && (
                <div className="flow-root animate-fadeIn">
                  <ul className="-mb-8">
                    {project.tasks &&
                      project.tasks.map((task, index) => {
                        const isOverdue = task.taskStatus === "Overdue";
                        const isAttention = task.taskStatus === "Attention";
                        const isInProgress = task.taskStatus === "In Progress";
                        const hasPulse = isInProgress || isAttention;

                        return (
                          <li key={index}>
                            <div className="relative pb-8">
                              {/* Vertical Connector Line */}
                              {index < project.tasks.length - 1 && (
                                <span
                                  className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                                  aria-hidden="true"
                                />
                              )}

                              <div className="relative flex space-x-3">
                                {/* Timeline Node with Status Indicator */}
                                <div>
                                  <span
                                    className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white ${getTimelineNodeColor(
                                      task.taskStatus
                                    )} ${hasPulse ? "animate-pulse-slow" : ""}`}
                                  >
                                    {getTimelineIcon(task.taskStatus)}
                                  </span>
                                </div>

                                {/* Task Card Content */}
                                <div className="min-w-0 flex-1 pt-1.5">
                                  <div
                                    className={`relative rounded-xl transition-all duration-200 group ${isOverdue
                                      ? "bg-white border-2 border-red-300 shadow-sm p-5 animate-pulse-glow-red"
                                      : isAttention
                                        ? "bg-white border-2 border-amber-300 shadow-sm p-5 animate-pulse-glow-amber"
                                        : "bg-white border border-gray-200 shadow-sm hover:shadow-md p-5"
                                      }`}
                                  >
                                    {/* Real content */}
                                    <div>
                                      <div className="flex flex-col lg:flex-row gap-6">
                                        {/* Task Info */}
                                        <div className="flex-1">
                                          <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center gap-3">
                                              <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 text-gray-600 font-bold text-sm">
                                                {task.taskNo}
                                              </span>
                                              <h3 className="text-lg font-bold text-gray-900">
                                                {task.subject}
                                              </h3>
                                            </div>
                                            <span
                                              className={`flex items-center px-3 py-1 rounded-full text-xs font-semibold border whitespace-nowrap ${getStatusColor(
                                                task.taskStatus
                                              )}`}
                                            >
                                              {getStatusIcon(task.taskStatus)}
                                              {task.taskStatus}
                                            </span>
                                          </div>

                                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 mb-4">
                                            <div className="flex items-center gap-2">
                                              <CountryFlagIcon
                                                country={task.country}
                                                size={22}
                                              />
                                              <span className="font-medium text-gray-900">
                                                {task.country}
                                              </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                              <Briefcase className="w-4 h-4 text-gray-400" />
                                              <span>{task.task}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                              <User className="w-4 h-4 text-gray-400" />
                                              <span>
                                                {task.analyst1}{" "}
                                                {task.analyst2 &&
                                                  `& ${task.analyst2}`}
                                              </span>
                                            </div>
                                          </div>

                                          {/* Financials Section (Conditional) */}
                                          {canViewFinancials && (
                                            <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-3 gap-4">
                                              <div className="bg-gray-50 rounded-lg p-3">
                                                <p className="text-xs text-gray-500 mb-1">
                                                  Fee
                                                </p>
                                                <p className="font-semibold text-gray-900">
                                                  {task.income}
                                                </p>
                                              </div>
                                              <div className="bg-gray-50 rounded-lg p-3">
                                                <p className="text-xs text-gray-500 mb-1">
                                                  VAT
                                                </p>
                                                <p className="font-semibold text-gray-900">
                                                  {task.vat}%
                                                </p>
                                              </div>
                                              <div className="bg-indigo-50 rounded-lg p-3">
                                                <p className="text-xs text-indigo-600 mb-1">
                                                  Total Inc VAT
                                                </p>
                                                <p className="font-bold text-indigo-700">
                                                  {task.amountIncVat}
                                                </p>
                                              </div>
                                            </div>
                                          )}
                                        </div>

                                        {/* Dates Section */}
                                        <div className="lg:w-72 flex flex-col gap-3 lg:border-l lg:border-gray-100 lg:pl-6">
                                          <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                                            <div className="flex items-center gap-2 text-gray-600">
                                              <Calendar className="w-4 h-4" />
                                              <span className="text-xs font-medium uppercase tracking-wide">
                                                Accepted
                                              </span>
                                            </div>
                                            <span className="font-semibold text-gray-900">
                                              {formatDate(task.orderAccepted)}
                                            </span>
                                          </div>

                                          <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                                            <div className="flex items-center gap-2 text-gray-600">
                                              <Clock className="w-4 h-4" />
                                              <span className="text-xs font-medium uppercase tracking-wide">
                                                Due Date
                                              </span>
                                            </div>
                                            <span className="font-semibold text-gray-900">
                                              {formatDate(task.dueDate)}
                                            </span>
                                          </div>

                                          <div
                                            className={`flex items-center justify-between p-3 rounded-lg transition-all duration-200 ${task.completedOn
                                              ? "bg-green-100 border border-green-200 shadow-sm"
                                              : "bg-gray-50 border border-transparent"
                                              }`}
                                          >
                                            <div
                                              className={`flex items-center gap-2 ${task.completedOn
                                                ? "text-green-800"
                                                : "text-gray-600"
                                                }`}
                                            >
                                              {task.completedOn ? (
                                                <Check className="w-5 h-5 text-green-600 stroke-[3]" />
                                              ) : (
                                                <CheckCircle className="w-4 h-4" />
                                              )}
                                              <span
                                                className={`text-xs font-bold uppercase tracking-wide ${task.completedOn
                                                  ? "text-green-800"
                                                  : ""
                                                  }`}
                                              >
                                                {task.completedOn
                                                  ? "Completed"
                                                  : "Status"}
                                              </span>
                                            </div>
                                            <div className="text-right">
                                              {editingCompletedOn.taskNo ===
                                                task.taskNo ? (
                                                <input
                                                  type="date"
                                                  value={
                                                    editingCompletedOn.value
                                                  }
                                                  onChange={
                                                    handleCompletedOnInputChange
                                                  }
                                                  onBlur={() =>
                                                    handleCompletedOnInputBlur(
                                                      task
                                                    )
                                                  }
                                                  autoFocus
                                                  className="text-sm border-b border-indigo-500 focus:outline-none bg-transparent w-32"
                                                />
                                              ) : (
                                                <span
                                                  onClick={() =>
                                                    handleEditCompletedOn(task)
                                                  }
                                                  className={`font-semibold cursor-pointer hover:text-indigo-600 transition-colors ${task.completedOn
                                                    ? "text-green-700"
                                                    : "text-gray-400"
                                                    }`}
                                                >
                                                  {task.completedOn
                                                    ? formatDate(
                                                      task.completedOn
                                                    )
                                                    : "Mark Done"}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </li>
                        );
                      })}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {pdfPreview.isOpen && (
        <PDFPreview
          url={pdfPreview.url}
          filename={pdfPreview.filename}
          onClose={closePdfPreview}
        />
      )}

      <Dialog
        open={deleteFileDialog.open}
        onClose={closeDeleteAttachmentDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
          },
        }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center backdrop-blur-sm">
              <DeleteOutline className="text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">
                Delete {deleteTargetType === "invoice" ? "Invoice" : "Document"}
              </h2>
              <p className="text-sm text-red-100">
                This action cannot be undone
              </p>
            </div>
          </div>
          <IconButton
            onClick={closeDeleteAttachmentDialog}
            disabled={isDeletingFile}
            sx={{ color: "white" }}
            className="hover:bg-white/10"
          >
            <Close />
          </IconButton>
        </div>

        {/* Content */}
        <div className="p-6 flex flex-col items-center justify-center">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-gray-700 text-sm">
              Are you sure you want to remove{" "}
              <span className="font-semibold text-gray-900">
                {deleteTargetName || "this file"}
              </span>{" "}
              from the project?
            </p>
          </div>
          {deleteFileError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
              {deleteFileError}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
          <button
            onClick={closeDeleteAttachmentDialog}
            disabled={isDeletingFile}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmDeleteAttachment}
            disabled={isDeletingFile}
            className="px-6 py-2 text-sm font-medium text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-lg shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeletingFile ? "Deleting..." : "Delete"}
          </button>
        </div>
      </Dialog>

      {/* Edit Project Modal */}
      <Dialog
        open={showEditModal}
        onClose={createNoBackdropCloseHandler(handleCloseEdit)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
            overflow: "hidden",
            borderRadius: "16px",
          },
        }}
      >
        {showEditModal && (
          <EditProject projectId={id} onClose={handleCloseEdit} />
        )}
      </Dialog>

      {/* Delete Project Dialog */}
      {showDeleteDialog && (
        <DeleteProjectDialog
          open={showDeleteDialog}
          onClose={handleCloseDeleteDialog}
          deletePassword={deletePassword}
          setDeletePassword={setDeletePassword}
          deleteError={deleteError}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {/* Project Summary Dialog */}
      <ProjectSummaryDialog
        open={isSummaryDialogOpen}
        onClose={handleCloseSummaryDialog}
        summaryPlainText={summaryPlainText}
        summaryHtmlText={summaryHtmlText}
        summaryFormat={summaryFormat}
        summaryCurrency={summaryCurrency}
        onFormatChange={handleSummaryFormatChange}
        onCurrencyChange={handleSummaryCurrencyChange}
        projectRef={project?.projectRef || ""}
        onCopy={handleCopySummary}
        hasCopied={hasCopiedSummary}
        isLoading={isSummaryLoading}
        stageText={summaryStageText}
      />

      {/* Invoice Dialog */}
      <InvoiceDialog
        open={isInvoiceDialogOpen}
        onClose={() => setIsInvoiceDialogOpen(false)}
        invoiceChoice={invoiceChoice}
        setInvoiceChoice={setInvoiceChoice}
        selectedCompany={selectedCompany}
        setSelectedCompany={setSelectedCompany}
        allowedCompanies={invoiceAllowedCompanies}
        useManualAED={useManualAED}
        setUseManualAED={setUseManualAED}
        showGBP={showGBP}
        setShowGBP={setShowGBP}
        exchangeRateSource={exchangeRateSource}
        setExchangeRateSource={setExchangeRateSource}
        exchangeRateAPIRate={exchangeRateAPIRate}
        cbuaeRate={cbuaeRate}
        loadingRates={loadingRates}
        isGeneratingInvoice={isGeneratingInvoice}
        onCreateInvoice={handleInvoiceCreation}
      />

      {/* Invoice Date Warning Dialog */}
      <Dialog
        open={showDateWarningDialog}
        onClose={() => setShowDateWarningDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
          },
        }}
      >
        {/* Header */}
        <div
          className={`${theme.formHeaderBg} px-6 py-4 flex items-center justify-between`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center backdrop-blur-sm">
              <WarningAmber className="text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">
                No Invoice Date Set
              </h2>
              <p className={`text-sm ${theme.formSubText}`}>Action required</p>
            </div>
          </div>
          <IconButton
            onClick={() => setShowDateWarningDialog(false)}
            sx={{ color: "white" }}
            className="hover:bg-white/10"
          >
            <Close />
          </IconButton>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
            <p className="text-gray-700 text-sm">
              This project does not have an invoice date set. The invoice will
              be generated without a date.
            </p>
          </div>
          <p className="text-gray-500 text-sm">
            Do you want to continue anyway?
          </p>
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
          <button
            onClick={() => setShowDateWarningDialog(false)}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={proceedWithInvoiceCreation}
            className={`px-6 py-2 text-sm font-medium text-white rounded-lg shadow-sm transition-all ${theme.toolbarAddButton}`}
          >
            Continue Anyway
          </button>
        </div>
      </Dialog>

      {/* Add Project Modal */}
      <Dialog
        open={openAdd}
        onClose={createNoBackdropCloseHandler(handleCloseAdd)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: "90vh",
            overflow: "hidden",
            borderRadius: "16px",
          },
        }}
      >
        <AddProject onClose={handleCloseAdd} />
      </Dialog>

      {/* Add Client Modal */}
      <Dialog
        open={openAddClient}
        onClose={createNoBackdropCloseHandler(handleCloseAddClient)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add Client</DialogTitle>
        <DialogContent>
          <AddClient onClose={handleCloseAddClient} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddClient} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={open} autoHideDuration={6000} onClose={handleClose}>
        <Alert onClose={handleClose} severity="success">
          Changes saved successfully!
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ProjectDetail;
